import Chart from './Chart'

import { select } from 'd3-selection'
import { scaleLinear } from 'd3-scale'
import { merge } from 'd3-array'
import { line } from 'd3-shape'

const d3 = {
  select,
  scaleLinear,
  merge,
  line,
}

export default class LineChart extends Chart {

  constructor(settings, data, id) {
    super(settings, data, id)

    this.settings = Object.assign({
      xScale: d3.scaleLinear(),
      strokeWidth: 1.5,
      showLegend1: false,
      showLegend2: false,
    }, this.settings)

    return this
  }

  setDimensions( svg ) {

    let width = svg.node().getBoundingClientRect().width

    width = Math.min(width, this.settings.width)

    let needsRedraw = this.forceRedraw || (this.width != width)
    this.forceRedraw = false

    this.width = width
    this.innerWidth = width - this.padding.left - this.padding.right


    return needsRedraw

  }

  draw() {

    let settings = this.settings

    let svg = d3.select( '#' + this.id )

    if( !this.setDimensions( svg ) ) return

    this.setScales()

    let container = svg
      .selectAll('g.chart-container').data([ this.data ])

    let containerEnter = container.enter().append('g')
      .attr('class', 'chart-container')

    let linesGroup = container.merge( containerEnter ).selectAll('g.lines')
        .data( this.data )

    let linesGroupEnter = linesGroup.enter().append('g')
        .attr('class', (d,i) => 'lines lines-group-' + i )
        .attr('transform', `translate( ${ this.padding.left }, ${ this.padding.top } )`)

    let line = d3.line()
      .x((d) => this.xScale( d.x ))
      .y((d) => this.yScale( +d.y ))

    let lines = linesGroupEnter.append('path')
        .style('fill', 'none')
        .style('stroke-width', this.settings.strokeWidth)

    let _this = this

    linesGroup.merge(linesGroupEnter).each(function(d,i) {
      d3.select(this).select('path')
        .attr('d', line(d.values) )
        .style('stroke', _this.settings.colors( d.title ))
        .attr('stroke-dasharray', i > 0 ? '5 5' : '')
        .attr('class', 'line-' + i )
    })

    this.drawXAxis(container.merge( containerEnter ))

    this.drawYAxis(container.merge( containerEnter ))

    this.drawLegend1(container.merge( containerEnter ))
    this.drawLegend2(container.merge( containerEnter ))

  }

  setScales() {

    this.xScale = this.settings.xScale
      .rangeRound([ 0, this.innerWidth ])

    let allData = this.data.map((d) => d.values)

    let yMax = Math.max.apply( Math, d3.merge( allData ).map((d) => d.y) )

    let yMin = Math.min.apply( Math, d3.merge( allData ).map((d) => d.y) )

    this.yScale = d3.scaleLinear()
      .domain([ Math.min( 0, yMin ), Math.max( 0, yMax ) ])
      .rangeRound([ this.innerHeight, 0 ])

  }

  drawLegend1(container) {
    if(!this.settings.showLegend1) return

    const legendData = this.data.filter((d) =>
      d.title == 'Panic Factors Simulation' || d.title == 'Balance Sheet Factors Simulation'
    )

    let legendContainer = container
        .selectAll('g.legend-1').data([ legendData ])

    let legendContainerEnter = legendContainer.enter().append('g')
        .attr('class', 'legend-1')
        .attr('font-size', 12)
        .attr('transform', 'translate(0,1)')
        .attr('text-anchor', 'start')

    let legendItems = legendContainer.merge( legendContainerEnter )
        .selectAll('g.legend-item').data((d) => d)

    let legendItemsEnter = legendItems.enter().append('g')
        .attr('class', 'legend-item')

    let rect = legendItemsEnter.append('rect')
        // .attr('x', 0)
        // .attr('y', 0)
        .attr('width', 12)
        .attr('height', 12)
        .style('fill', (d,i) => this.settings.colors(d.title))

    legendItemsEnter.append('text')
        .attr('x', 16)
        .attr('y', 6)
        .attr('dy', '0.32em')
        .text((d) => d.title)

    let _this = this,
      previousRight = 0,
      top = 0.5

    legendItems.merge( legendItemsEnter )
      .each(function(d,i) {

        if( previousRight + this.getBoundingClientRect().width > _this.innerWidth ) {
          top += 18
          previousRight = 0
        }

        d3.select(this)
          .attr('transform', `translate(${ _this.padding.left + previousRight },${ top })`)

        previousRight = this.getBoundingClientRect().width + previousRight + 8
      })

    this.legend1Right = previousRight

  }

  drawLegend2(container) {
    if(!this.settings.showLegend2) return

    const legendData = this.data.filter((d) =>
      d.title != 'Panic Factors Simulation' && d.title != 'Balance Sheet Factors Simulation'
    )

    let legendContainer = container
        .selectAll('g.legend-2').data([ legendData ])

    let legendContainerEnter = legendContainer.enter().append('g')
        .attr('class', 'legend-2')
        .attr('font-size', 12)
        .attr('transform', 'translate(0,1)')
        .attr('text-anchor', 'start')

    let legendItems = legendContainer.merge( legendContainerEnter )
        .selectAll('g.legend-item').data((d) => d)

    let legendItemsEnter = legendItems.enter().append('g')
        .attr('class', 'legend-item')
        .attr('transform', 'translate(0,1)')

    let rect = legendItemsEnter.append('rect')
        // .attr('x', 0)
        // .attr('y', 0)
        .attr('width', 12)
        .attr('height', 12)
        .style('fill', (d,i) => this.settings.colors( d.title ))

    legendItemsEnter.append('text')
        .attr('x', 16)
        .attr('y', 6)
        .attr('dy', '0.32em')
        .text((d) => d.title)

    let _this = this,
      previousLeft = 0,
      top = 0.5

    legendItems.merge( legendItemsEnter )
      .each(function(d,i) {

        let leftPadding = 8 * ( i ? 1 : 0 )
        previousLeft = this.getBoundingClientRect().width + previousLeft + leftPadding


        d3.select(this)
          .attr('transform', `translate(${ _this.innerWidth + _this.padding.left - previousLeft },${ top })`)
      })


  }

  setColors(colors) {
    this.settings.colors = colors
  }

}
